/**
 * Shows count of "orphan" active words — words the user has mastered
 * but aren't in any curriculum level (A1..C2). These are genuine
 * vocabulary (typos excluded, surprisingly few): proper nouns, loan
 * words, conversational fillers that slipped through before the
 * curriculum was updated, inflections that didn't match any lemma.
 *
 * Informational — the user earned these but they don't count toward
 * CEFR coverage. Useful for transparency.
 */
import { useState } from "react";
import type { OrphanWord } from "../../api";

export default function OrphanWordsPanel({
  count,
  sample,
}: {
  count: number;
  sample: OrphanWord[];
}) {
  const [expanded, setExpanded] = useState(false);

  if (count === 0) {
    return (
      <div className="card">
        <div className="mb-2">
          <div className="text-xs text-ink-faint uppercase tracking-[0.14em]">
            off-curriculum words
          </div>
        </div>
        <div className="text-ink-faint text-sm py-3">
          None. Every word you've mastered is tracked against the CEFR
          curriculum.
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="mb-2">
        <div className="text-xs text-ink-faint uppercase tracking-[0.14em]">
          off-curriculum words
        </div>
        <div className="text-[11px] text-ink-faint mt-0.5">
          mastered but not on any A1–C2 list
        </div>
      </div>
      <div className="flex items-baseline gap-2 mb-3">
        <div className="font-serif text-3xl text-ink">{count}</div>
        <div className="text-xs text-ink-soft">
          word{count !== 1 ? "s" : ""}
        </div>
      </div>
      <p className="text-[11px] text-ink-soft leading-relaxed mb-3">
        Proper nouns, loanwords, inflected forms, and fillers you picked up
        from chat. They don't count toward your CEFR level progress but
        they're real German you know.
      </p>
      <div className="flex flex-wrap gap-1.5">
        {(expanded ? sample : sample.slice(0, 8)).map((w) => (
          <span
            key={w.word}
            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-sm bg-paper-warm/40 text-ink"
            title={`mastery ${w.mastery}, ${w.reps} reps`}
          >
            {w.word}
          </span>
        ))}
      </div>
      {sample.length > 8 ? (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 text-[11px] text-terracotta hover:underline"
        >
          {expanded
            ? "Show fewer"
            : `Show ${sample.length - 8} more${count > sample.length ? ` (of ${count})` : ""}`}
        </button>
      ) : null}
    </div>
  );
}
